import React, { FC } from 'react'
import { Box, makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { AuctionShipment } from 'apps/fleet-planner/types'
import {
  getMarketplaceValues,
  getAuctionshipmentStops,
  formatMpTotalMiles,
  formatMpEquipment,
  formatMpWeight,
} from 'apps/fleet-planner/utils/marketplaceHelpers'
import { Bullet } from 'components/atoms'
import { formatPerMile, formatCurrency } from 'utils/formatCurrency'
import { AppointmentMobile } from './AppointmentMobile'

const useMarketplaceCardRowStyles = makeStyles(() => ({
  bid: {
    color: brandColors.info,
  },
}))

const getBidMessage = (bids: AuctionShipment['bids']): string => {
  if (bids.length === 1 && bids[0]) {
    const { amount } = bids[0] || {}
    return `You bid ${formatCurrency(amount)}`
  }
  if (bids.length > 1) {
    return `You have made ${bids.length} bids`
  }
  return ''
}

interface IMarketplaceCardRow {
  shipment: AuctionShipment
  isEquipmentSectionVisible?: boolean
}

export const MarketplaceDetails: FC<IMarketplaceCardRow> = ({
  isEquipmentSectionVisible = true,
  shipment,
}) => {
  const { bids = [] } = shipment || {}
  const { isMobile } = useBreakpointContext()
  const classes = useMarketplaceCardRowStyles()
  const { targetRate = '', totalMiles = '' } = getMarketplaceValues(shipment)
  const { stops = [] } = getAuctionshipmentStops(shipment)
  const formattedMiles = parseFloat(totalMiles)
  return (
    <Box>
      <Box display='flex' pt={isMobile ? 1 : 0}>
        <Box>
          <Typography variant='h3'>{formatCurrency(targetRate)}</Typography>
          {bids.length ? (
            <Typography variant='subtitle2' className={classes.bid}>
              {getBidMessage(bids)}
            </Typography>
          ) : (
            <Typography variant='body2' color='textSecondary'>
              ${formatPerMile(targetRate, formattedMiles, true)}/mi
            </Typography>
          )}
        </Box>
        {isEquipmentSectionVisible && (
          <Box display='flex' flexDirection='column' marginLeft='auto'>
            <Typography align='right' variant='button'>
              {formatMpTotalMiles(shipment)}
            </Typography>
            <Typography variant='caption'>
              {formatMpEquipment(shipment)} <Bullet /> {formatMpWeight(shipment)}
            </Typography>
          </Box>
        )}
      </Box>
      <Box>
        {stops.map(stop => {
          const { city, endAt, startAt, stateCode, stopSequence, stopType, timezone } = stop || {}
          const key = `${stopType}${stopSequence}`
          return (
            <Box display='flex' flexDirection='row' key={key}>
              <Box display='flex' alignItems='center' marginTop={1}>
                <AppointmentMobile
                  key={key}
                  startAt={startAt}
                  endAt={endAt}
                  timezone={timezone}
                  city={city}
                  stateCode={stateCode}
                  mapMarkerType={stopType}
                />
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
